import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt$(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

function fmtDate(s) {
  if (!s) return 'Not set yet';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function useCountdown(endDateStr) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!endDateStr) return;
    const tick = () => {
      const end  = new Date(endDateStr + 'T23:59:59').getTime();
      const now  = Date.now();
      const diff = end - now;
      if (diff <= 0) { setRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }); return; }
      const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setRemaining({ days, hours, minutes, seconds, expired: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDateStr]);

  return remaining;
}

function CountdownBlock({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none">
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-xs text-purple-200 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function CountdownSep() {
  return <div className="text-2xl font-bold text-purple-300 self-start mt-1">:</div>;
}

export default function UnderContract() {
  const { token } = useAuth();
  const navigate        = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loanStage, setLoanStage] = useState(null);

  const countdown = useCountdown(record?.option_end_date);

  const load = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/option-period/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRecord(r.data.record))
      .catch(() => setRecord(null))
      .finally(() => setLoading(false));

    axios
      .get(`${API_URL}/api/borrower/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLoanStage(r.data.loan_stage))
      .catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const TASKS = [
    {
      icon: '🏠',
      title: 'Home Appraisal',
      desc: 'A licensed appraiser will visit the property to determine fair market value.',
    },
    {
      icon: '🔍',
      title: 'Home Inspection',
      desc: 'A certified inspector will evaluate the condition of the property.',
    },
    {
      icon: '📋',
      title: 'Review Reports',
      desc: 'Review your appraisal package findings with your Doorly advisor.',
    },
    {
      icon: '✅',
      title: 'Make Your Decision',
      desc: 'Proceed to closing, request seller credit, or cancel within the option period.',
    },
  ];

  return (
    <AppShell>
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <svg className="animate-spin w-8 h-8 text-[#3a6ad9]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto">
          {/* Congrats banner */}
          <div className="rounded-2xl overflow-hidden mb-8" style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}>
            <div className="px-6 py-8 sm:px-8">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                Stage 8 of 10
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                🎉 You're Under Contract!
              </h1>
              <p className="text-purple-200 text-sm leading-relaxed">
                Your offer has been accepted. The next step is the Option Period, where you'll complete your appraisal package and review before making your final decision.
              </p>
            </div>

            {/* Countdown */}
            {record?.option_end_date && (
              <div className="border-t border-white/20 px-6 py-6 sm:px-8">
                <p className="text-purple-200 text-xs uppercase tracking-wider mb-4 font-semibold">
                  {countdown?.expired ? 'Option Period Ended' : 'Option Period Ends In'}
                </p>
                {countdown && !countdown.expired ? (
                  <div className="flex items-start gap-3 sm:gap-4">
                    <CountdownBlock label="Days"    value={countdown.days}    />
                    <CountdownSep />
                    <CountdownBlock label="Hours"   value={countdown.hours}   />
                    <CountdownSep />
                    <CountdownBlock label="Minutes" value={countdown.minutes} />
                    <CountdownSep />
                    <CountdownBlock label="Seconds" value={countdown.seconds} />
                  </div>
                ) : (
                  <p className="text-white font-bold text-lg">Option period has expired.</p>
                )}
                <p className="text-purple-300 text-xs mt-3">Deadline: {fmtDate(record.option_end_date)}</p>
              </div>
            )}
          </div>

          {/* Property details */}
          {record ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Your Property</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Address</p>
                  <p className="font-semibold text-black">{record.property_address}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Purchase Price</p>
                  <p className="font-semibold text-black">{fmt$(record.purchase_price)}</p>
                </div>
                {record.contract_date && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Contract Date</p>
                    <p className="font-semibold text-black">{fmtDate(record.contract_date)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-400 mb-1">Option Period</p>
                  <p className="font-semibold text-black">{record.option_period_days} days</p>
                </div>
                {record.option_end_date && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Option Deadline</p>
                    <p className="font-semibold text-black">{fmtDate(record.option_end_date)}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-semibold text-yellow-800 text-sm">Property Details Pending</p>
                  <p className="text-yellow-700 text-sm mt-1">Your Doorly advisor will add your property details shortly.</p>
                </div>
              </div>
            </div>
          )}

          {/* What to expect */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-5">What Happens During the Option Period</h2>
            <div className="space-y-4">
              {TASKS.map((task, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: '#f5f3ff' }}>
                    {task.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-black text-sm">{task.title}</p>
                    <p className="text-gray-500 text-sm mt-0.5">{task.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            {loanStage === 'option_period' ? (
              <button
                onClick={() => navigate('/portal/option-period')}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}
              >
                Enter Option Period
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="flex-1 flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-5 py-3.5">
                <svg className="w-5 h-5 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-purple-700 font-medium">Option Period tasks will be unlocked by your advisor.</p>
              </div>
            )}
            <Link
              to="/dashboard"
              className="px-6 py-3.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 text-center transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>

          {/* Help */}
          <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eff3fe' }}>
              <svg className="w-5 h-5 text-[#3a6ad9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-black text-sm">Questions about your contract?</p>
              <p className="text-gray-500 text-sm">Use the chat button below or{' '}
                {/* The help centre is MOVING FROM INTERCOM TO A NATIVE BUILD AT THIS SAME
    SUBDOMAIN. Do not remove this link during Intercom cleanup — the destination
    changes underneath it, the URL does not. See
    backend/knowledge/docs/help-export/MIGRATION.md for the cutover order. */}
                <a href="https://help.godoorly.com" target="_blank" rel="noreferrer" className="text-[#3a6ad9] hover:underline font-medium">visit our help center</a>.
              </p>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
